import "./sidebar.scss";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import StoreIcon from "@mui/icons-material/Store";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import BuildIcon from "@mui/icons-material/Build";
import DescriptionIcon from "@mui/icons-material/Description";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useLocation } from "react-router-dom";
import { DarkModeContext } from "../../context/darkModeContext";
import { useContext, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionsContext";
import logo from "../../img/logo.png";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const { version } = require("../../../package.json");

interface MenuItem {
  icon: JSX.Element;
  text: string;
  link: string;
  module: string;
  badge?: number;
}

interface MenuSection {
  title: string;
  items: MenuItem[];
  collapsible?: boolean;
}

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen = false,
  onClose,
  isMobile = false,
}) => {
  const { darkMode } = useContext(DarkModeContext);
  const { user, logout } = useAuth();
  const { hasModuleAccess } = usePermissions();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["PRINCIPAL", "LISTAS", "CONFIGURACIÓN"])
  );

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => {
      const s = new Set(prev);
      s.has(title) ? s.delete(title) : s.add(title);
      return s;
    });
  };

  // Close sidebar when a menu item is clicked on mobile
  const handleItemClick = useCallback(() => {
    if (isMobile && onClose) onClose();
  }, [isMobile, onClose]);

  const menuItems: MenuSection[] = [
    {
      title: "PRINCIPAL",
      collapsible: false,
      items: [
        { icon: <DashboardIcon />, text: "Dashboard", link: "/", module: "/" },
      ],
    },
    {
      title: "LISTAS",
      collapsible: true,
      items: [
        { icon: <PersonOutlineIcon />, text: "Usuarios", link: "/usuarios", module: "/usuarios" },
        { icon: <StoreIcon />, text: "Almacén", link: "/almacen", module: "/almacen" },
        { icon: <PointOfSaleIcon />, text: "Ventas", link: "/ventas", module: "/ventas" },
        { icon: <ShoppingCartIcon />, text: "Compras", link: "/compras", module: "/compras" },
        { icon: <RequestQuoteIcon />, text: "Cotizaciones", link: "/cotizaciones", module: "/cotizaciones" },
      ],
    },
    {
      title: "DEVOLUCIONES",
      collapsible: true,
      items: [
        { icon: <AssignmentReturnIcon />, text: "Dev. Compra", link: "/devoluciones-compra", module: "/devoluciones-compra" },
        { icon: <AssignmentReturnIcon />, text: "Dev. Venta", link: "/devoluciones-venta", module: "/devoluciones-venta" },
      ],
    },
    {
      title: "REPORTES",
      collapsible: true,
      items: [
        { icon: <BarChartIcon />, text: "Estadísticas", link: "/reportes", module: "/reportes" },
      ],
    },
    {
      title: "CONFIGURACIÓN",
      collapsible: true,
      items: [
        { icon: <SettingsIcon />, text: "Administración", link: "/administracion", module: "/administracion" },
        { icon: <BuildIcon />, text: "Mantenimiento", link: "/mantenimiento", module: "/mantenimiento" },
        { icon: <DescriptionIcon />, text: "Facturas", link: "/facturas", module: "/facturas" },
      ],
    },
  ];

  const isActiveRoute = (link: string) => {
    if (link === "/") return location.pathname === "/";
    return location.pathname.startsWith(link);
  };

  const sidebarClasses = [
    "sidebar",
    darkMode ? "dark" : "",
    isMobile && isOpen ? "open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={sidebarClasses}>
      {/* ── Top / Logo ── */}
      <div className="top">
        <Link to="/" className="logo-link" onClick={handleItemClick}>
          <img src={logo} alt="MANTIS" className="logo" />
          <span className="logo-text">MANTIS</span>
        </Link>
        {/* Close button (shown on mobile via CSS) */}
        <button
          className="sidebar-close-btn"
          onClick={onClose}
          aria-label="Cerrar menú"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="divider" />

      {/* ── Menu ── */}
      <div className="center">
        <ul>
          {menuItems.map((section) => {
            const hasAccessibleItems = section.items.some((item) =>
              hasModuleAccess(item.module)
            );
            if (!hasAccessibleItems) return null;

            const isExpanded = expandedSections.has(section.title);

            return (
              <div key={section.title} className="menu-section">
                <div
                  className={`section-title ${section.collapsible ? "collapsible" : ""}`}
                  onClick={() => section.collapsible && toggleSection(section.title)}
                >
                  <span>{section.title}</span>
                  {section.collapsible &&
                    (isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />)}
                </div>

                {(!section.collapsible || isExpanded) && (
                  <div className="menu-items">
                    {section.items.map((item) => {
                      if (!hasModuleAccess(item.module)) return null;
                      const isActive = isActiveRoute(item.link);

                      return (
                        <Link
                          to={item.link}
                          key={item.text}
                          className="menu-item-link"
                          onClick={handleItemClick}
                        >
                          <li className={`menu-item ${isActive ? "active" : ""}`}>
                            <div className="icon-wrapper">{item.icon}</div>
                            <span className="item-text">{item.text}</span>
                            {item.badge && item.badge > 0 && (
                              <span className="badge">{item.badge}</span>
                            )}
                          </li>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <div className="divider" />

          {/* Mi Perfil */}
          <Link
            to={`/perfil/${user?.Id}`}
            className="menu-item-link"
            onClick={handleItemClick}
          >
            <li
              className={`menu-item ${location.pathname.includes("/perfil/") ? "active" : ""}`}
            >
              <div className="icon-wrapper">
                <AccountCircleIcon />
              </div>
              <span className="item-text">Mi Perfil</span>
            </li>
          </Link>

          {/* Cerrar Sesión */}
          <li className="menu-item logout" onClick={logout}>
            <div className="icon-wrapper">
              <ExitToAppIcon />
            </div>
            <span className="item-text">Cerrar Sesión</span>
          </li>
        </ul>
      </div>

      {/* ── Footer ── */}
      <div className="footer">
        <div className="version-info">v{version}</div>
      </div>
    </div>
  );
};

export default Sidebar;
